import { getNumericChainId } from "../model/NetworksDetail";

/**
 * Discovers an EIP-6963 provider by RDNS string.
 * @param {string} targetRdns 
 * @param {number} timeoutMs 
 * @returns {Promise<any|null>}
 */
export function discoverEIP6963Provider(targetRdns, timeoutMs = 600) {
    return new Promise((resolve) => {
        let isResolved = false;

        const onAnnounceProvider = (event) => {
            try {
                if (event && event.detail && event.detail.info && event.detail.info.rdns === targetRdns) {
                    cleanup();
                    isResolved = true;
                    resolve(event.detail.provider);
                }
            } catch (err) {
                console.error("[EIP-6963] Error handling announceProvider event:", err);
            }
        };

        const timer = setTimeout(() => {
            cleanup();
            if (!isResolved) {
                isResolved = true;
                resolve(null);
            }
        }, timeoutMs);

        const cleanup = () => {
            clearTimeout(timer);
            if (typeof window !== "undefined") {
                window.removeEventListener("eip6963:announceProvider", onAnnounceProvider);
            }
        };

        if (typeof window !== "undefined") {
            window.addEventListener("eip6963:announceProvider", onAnnounceProvider);
            window.dispatchEvent(new Event("eip6963:requestProvider"));
        } else {
            resolve(null);
        }
    });
}

/**
 * Resolves KUB Wallet injected provider (EIP-6963 rdns "com.kubwallet" or injected object).
 * @returns {Promise<any|null>}
 */
export async function getInjectedKubWalletProvider() {
    // 1. Try EIP-6963 exact RDNS
    const eipProvider = await discoverEIP6963Provider("com.kubwallet", 500);
    if (eipProvider) {
        return eipProvider;
    }

    // 2. Try window.kubwallet if injected directly
    if (typeof window !== "undefined" && window.kubwallet) {
        return window.kubwallet;
    }

    // 3. Check window.ethereum providers array
    if (typeof window !== "undefined" && window.ethereum) {
        if (Array.isArray(window.ethereum.providers)) {
            const kubProvider = window.ethereum.providers.find(p => p.isKubWallet);
            if (kubProvider) return kubProvider;
        } else if (window.ethereum.isKubWallet) {
            return window.ethereum;
        }
    }

    return null;
}

/**
 * Resolves MetaMask provider explicitly, avoiding accidental selection of other injected wallets.
 * @returns {Promise<any|null>}
 */
export async function getMetaMaskProvider() {
    // 1. Try EIP-6963 exact RDNS
    const eipProvider = await discoverEIP6963Provider("io.metamask", 500);
    if (eipProvider) {
        return eipProvider;
    }

    // 2. Check window.ethereum
    if (typeof window !== "undefined" && window.ethereum) {
        if (Array.isArray(window.ethereum.providers)) {
            const mmProvider = window.ethereum.providers.find(p => p.isMetaMask && !p.isKubWallet);
            if (mmProvider) return mmProvider;
        } else if (window.ethereum.isMetaMask && !window.ethereum.isKubWallet) {
            return window.ethereum;
        }
    }

    return null;
}

/**
 * Requests accounts from the given provider.
 * @param {any} provider 
 * @returns {Promise<string[]>}
 */
export async function requestAccounts(provider) {
    if (!provider || typeof provider.request !== "function") {
        throw new Error("Provider does not support request()");
    }
    // WalletConnect provides approved accounts with the session. A second
    // eth_requestAccounts request can wait for another wallet interaction.
    const accounts = provider.isWalletConnect && Array.isArray(provider.accounts)
        ? provider.accounts
        : await provider.request({ method: "eth_requestAccounts" });
    if (!accounts || accounts.length === 0) {
        throw new Error("No account returned from wallet");
    }
    return accounts;
}

/**
 * Gets currently connected accounts without prompting.
 * @param {any} provider 
 * @returns {Promise<string[]>}
 */
export async function getAccounts(provider) {
    if (!provider || typeof provider.request !== "function") {
        return [];
    }
    try {
        return (await provider.request({ method: "eth_accounts" })) || [];
    } catch (e) {
        return [];
    }
}

/**
 * Gets numeric chain ID from provider.
 * @param {any} provider 
 * @returns {Promise<number>}
 */
export async function getChainId(provider) {
    if (!provider || typeof provider.request !== "function") {
        throw new Error("Provider does not support request()");
    }
    if (provider.isWalletConnect && Number.isInteger(provider.chainId)) {
        return provider.chainId;
    }
    const chainIdHex = await provider.request({ method: "eth_chainId" });
    if (typeof chainIdHex === "number") {
        return chainIdHex;
    }
    return parseInt(chainIdHex, 16);
}

/**
 * Gets native coin balance for account in integer wei.
 * @param {any} provider 
 * @param {string} account 
 * @returns {Promise<number>}
 */
export async function getBalance(provider, account) {
    if (!provider || typeof provider.request !== "function") {
        throw new Error("Provider does not support request()");
    }
    const balanceHex = await provider.request({
        method: "eth_getBalance",
        params: [account, "latest"],
    });
    return parseInt(balanceHex, 16);
}

/**
 * Ensures the provider is connected to the configured chain.
 * Handles wallet_switchEthereumChain, 4902 add-chain fallback, and 4001 user rejection.
 * @param {any} provider 
 * @param {object} chainDetail 
 * @returns {Promise<boolean>}
 */
export async function ensureChain(provider, chainDetail) {
    if (!provider || typeof provider.request !== "function") {
        throw new Error("Invalid provider passed to ensureChain");
    }

    const targetChainIdNum = getNumericChainId(chainDetail);
    const currentChainId = await getChainId(provider);

    if (currentChainId === targetChainIdNum) {
        return true;
    }

    try {
        await provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: chainDetail.chainId }],
        });
    } catch (switchError) {
        // Error code 4902 indicates that the chain has not been added to MetaMask / wallet
        const isChainMissing = switchError && (
            switchError.code === 4902 ||
            (switchError.data && switchError.data.originalError && switchError.data.originalError.code === 4902) ||
            /unrecognized chain|not added/i.test(switchError.message || "")
        );

        if (isChainMissing) {
            try {
                await provider.request({
                    method: "wallet_addEthereumChain",
                    params: [chainDetail],
                });
            } catch (addError) {
                if (addError && addError.code === 4001) {
                    const err = new Error("User rejected adding the network");
                    err.code = 4001;
                    throw err;
                }
                throw addError;
            }
        } else if (switchError && switchError.code === 4001) {
            const err = new Error("User rejected switching the network");
            err.code = 4001;
            throw err;
        } else {
            throw switchError;
        }
    }

    // Verify chain after switch
    const verifiedChainId = await getChainId(provider);
    if (verifiedChainId !== targetChainIdNum) {
        throw new Error(`Chain mismatch after switch. Expected ${targetChainIdNum}, got ${verifiedChainId}`);
    }

    return true;
}
